import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { SmartPaymentSolutionService } from './smart-payment-link.service';
import { WorkspaceService } from './workspace.service';

/**
 * Workspace names/codes that identify Sidney. Matched case-insensitively
 * against both `workspaceCode` and `name` of the workspace record.
 */
const SIDNEY_WORKSPACE_NAMES = ['sidney', 'sidney 2000'];

/**
 * Resolves whether the current payment session belongs to Sidney's workspace.
 *
 * Resolution order:
 *  1. workspaceId from GET /api/v1/smart-payment-solutions/{shortLink}
 *  2. workspaceId from GET /api/v1/smart-payment-solutions/by-order/{orderId}
 *  3. workspaceId persisted in localStorage by the short link loader
 *
 * The workspace is then confirmed either by matching the configured Sidney
 * workspace id or by matching the workspace name/code from
 * GET /api/v1/workspaces/{workspaceId}.
 *
 * The result is resolved once per session and replayed to every subscriber,
 * so callers can ask for it as many times as they need without extra requests.
 */
@Injectable({
  providedIn: 'root',
})
export class SidneyWorkspaceService {
  private resolution$?: Observable<boolean>;

  constructor(
    private smart: SmartPaymentSolutionService,
    private workspaces: WorkspaceService,
  ) {}

  /**
   * Emits true when the current session runs on Sidney's workspace.
   * Never errors: any failure resolves to false so the standard flow survives.
   */
  public isSidneyWorkspace(
    shortLink?: string | null,
    orderId?: string | null,
  ): Observable<boolean> {
    if (!this.resolution$) {
      this.resolution$ = this.resolveWorkspaceId(shortLink, orderId).pipe(
        switchMap((workspaceId) => this.matchesSidney(workspaceId)),
        catchError((err) => {
          console.warn('[SidneyWorkspace] Resolution failed, assuming non-Sidney workspace', err);
          return of(false);
        }),
        tap((isSidney) =>
          console.log('[SidneyWorkspace] Resolved isSidneyWorkspace =', isSidney),
        ),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }
    return this.resolution$;
  }

  /** Clears the cached resolution (used when a new short link is loaded). */
  public reset(): void {
    this.resolution$ = undefined;
  }

  private resolveWorkspaceId(
    shortLink?: string | null,
    orderId?: string | null,
  ): Observable<string> {
    const link = shortLink || this.readStorage('shortLink');
    const order = orderId || this.readStorage('orderId');

    return this.workspaceIdFromShortLink(link).pipe(
      switchMap((fromLink) =>
        fromLink ? of(fromLink) : this.workspaceIdFromOrder(order),
      ),
      map((workspaceId) => {
        const resolved = workspaceId || this.readStorage('workSpaceId');
        console.log('[SidneyWorkspace] Resolved workspaceId:', resolved, {
          shortLink: link,
          orderId: order,
        });
        return resolved;
      }),
    );
  }

  private workspaceIdFromShortLink(shortLink: string): Observable<string> {
    if (!shortLink) {
      return of('');
    }
    return this.smart.getSmartPaymentSolution(shortLink).pipe(
      map((res) => this.extractWorkspaceId(res)),
      catchError((err) => {
        console.warn('[SidneyWorkspace] getSmartPaymentSolution failed, falling back to by-order', err);
        return of('');
      }),
    );
  }

  private workspaceIdFromOrder(orderId: string): Observable<string> {
    if (!orderId) {
      return of('');
    }
    return this.smart.getSmartPaymentSolutionByOrder(orderId).pipe(
      map((res) => this.extractWorkspaceId(res)),
      catchError((err) => {
        console.warn('[SidneyWorkspace] getSmartPaymentSolutionByOrder failed', err);
        return of('');
      }),
    );
  }

  private extractWorkspaceId(res: any): string {
    const data = res?.data ?? res?.result ?? res;
    const workspaceId = data?.workSpaceId ?? data?.workspaceId ?? '';
    return workspaceId ? String(workspaceId) : '';
  }

  private matchesSidney(workspaceId: string): Observable<boolean> {
    if (!workspaceId) {
      console.warn('[SidneyWorkspace] No workspaceId available, assuming non-Sidney workspace');
      return of(false);
    }

    const configuredId = environment.SidneyWorkspaceId || '';
    if (configuredId && workspaceId.toLowerCase() === configuredId.toLowerCase()) {
      console.log('[SidneyWorkspace] workspaceId matches the configured Sidney workspace id');
      return of(true);
    }

    return this.workspaces.getWorkspaceById(workspaceId).pipe(
      map((res) => {
        const workspace = res?.data ?? res?.result ?? res;
        const code = this.normalize(workspace?.workspaceCode);
        const name = this.normalize(workspace?.name);
        const isSidney =
          SIDNEY_WORKSPACE_NAMES.includes(code) || SIDNEY_WORKSPACE_NAMES.includes(name);
        console.log('[SidneyWorkspace] Workspace identity:', { code, name, isSidney });
        return isSidney;
      }),
      catchError((err) => {
        console.warn('[SidneyWorkspace] getWorkspaceById failed', err);
        return of(false);
      }),
    );
  }

  private normalize(value: any): string {
    return typeof value === 'string' ? value.trim().toLowerCase() : '';
  }

  private readStorage(key: string): string {
    try {
      return localStorage.getItem(key) || '';
    } catch (e) {
      return '';
    }
  }
}
